import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { pluralReadableTypeName, readableTypeName } from '@iotv/core-services';
import { camelToDisplay, DeviceClassName, Milliseconds, NodeEdgeMapper, UserClassName } from '@iotv/datamodel-core';
import { deviceEnabledTypes } from '@iotv/payload-definition';
import { Card, Divider, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { getGraphQueryRequestV2 } from '../../../data/daoFunctions/GraphQueries/recursiveFilteredGraphQuery';
import { iterateQueryWithSource } from '../../../data/Wideawake/WideawakeWrapper';
import { hasHyperUser } from '../../../helpers/access';
import { inFirstOnly } from '../../../helpers/filters';
import { ActionIcons } from '../../../Iconography';
import { DialogWrapper } from '../../common/DiaglogWrapper';
import { getGatewayDevices } from '../../DeviceManagement/tabs/Wideawake/GatewayDevices';
import { NWName } from '../../factories/DataGrid';
import { getCustomersGatewaysQuery } from '../../Gateway/Card/query';
import ExpandableTable from '../Expandable/ExpandableTable';
import DeviceCount from './DeviceCount';
import { customerDefinition, deviceDefinition, DeviceExport, gatewayDefinition, offlineExport } from './DeviceDefinitions';
import { getAllUser, getDeviceEntityGroupCustomer } from './queries';
const debug = process.env.REACT_APP_DEBUG;
const entityTypes = Object.keys(deviceEnabledTypes);
const constructPairingEdge = (parent, child) => {
    return { ...child, pk: parent.pk };
};
/**
 * @link https://docs.google.com/document/d/17g9jdEiXIqq0AOLpeTpCw0fTOtDZEW_B5omBXQXwUCU/edit
 */
const DeviceSummaryCard = (props) => {
    const { userCustomers } = props;
    const now = new Date();
    const [{ userGroupRoles }] = useContext(AppMainViewContext);
    const hasHyper = hasHyperUser(userGroupRoles);
    const [customers, setCustomers] = useState([]);
    const [gatewayDevices, setGatewayDevices] = useState(undefined);
    // customer -> device
    const [wideawakeMapper, setWideAwakeMapper] = useState(undefined);
    const [offlineComponents, setOfflineComponents] = useState();
    const [onlineComponents, setOnlineComponets] = useState();
    const [errorComponents, setErrorComponents] = useState();
    const [usersList, setUserList] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    const filterForOffline = (deviceList) => deviceList.filter((device) => device.isAWOL);
    const filterForError = (deviceList) => deviceList.filter((device) => device.error);
    const makeDevicePairings = (gatewayList) => {
        const pairing = gatewayList.map(async (gateway) => {
            const gatewayDevs = await getGatewayDevices({
                sourceNode: gateway,
                ExclusiveStartKey: undefined,
                Limit: undefined,
            });
            return {
                parent: gateway,
                children: gatewayDevs.filter((gatewayDev) => gatewayDev.type === DeviceClassName),
            };
        });
        Promise.all(pairing).then((res) => {
            const objList = res
                .map((pair) => {
                // construct edges
                const parent = pair.parent;
                let children = pair.children;
                children = children.map((device) => {
                    const isAWOL = now.getTime() - device.lastSeen > Milliseconds.DAY;
                    return { ...device, isAWOL: isAWOL };
                });
                const edges = children.map((child) => {
                    return constructPairingEdge(parent, child);
                });
                return [...children, ...edges, parent];
            })
                .flat();
            const mapper = new NodeEdgeMapper();
            mapper.loadItems(objList);
            setGatewayDevices(mapper);
        });
    };
    const getFilteredGateway = (gatewayList) => gatewayList.filter((gateway) => gateway.isAWOL || now.getTime() - gateway.lastSeen > Milliseconds.DAY);
    /**
     * Set customers, and tgate their
     */
    const updateDeviceGateways = () => {
        const customerNEM = userCustomers.getNodesOfType('Customer') || [];
        setCustomers(customerNEM);
        if (customerNEM.length !== 0) {
            getGraphQueryRequestV2({
                contextObjects: customerNEM,
                query: getCustomersGatewaysQuery().query,
            }).then((gatewaysPromise) => {
                if (gatewaysPromise.err === null && gatewaysPromise.data !== undefined) {
                    const gateways = (gatewaysPromise.data?.getNodesOfType('Gateway') || []).filter((val) => val.eui !== undefined);
                    makeDevicePairings(getFilteredGateway(gateways));
                }
            });
        }
    };
    useEffect(() => {
        const combinedMapper = new NodeEdgeMapper();
        const getAllDetails = async () => {
            // get all devices
            const params = [{ key: 'pk', value: `${DeviceClassName}:`, predicate: 'begins_with' }];
            let allDevices = (await iterateQueryWithSource(DeviceClassName, true, params));
            allDevices = allDevices.filter((val) => val.eui !== undefined && val.eui.length === 16);
            const arrays = [], size = 50;
            while (allDevices.length > 0)
                arrays.push(allDevices.splice(0, size));
            const devEntPromises = await arrays.map((segment) => {
                return getGraphQueryRequestV2({ contextObjects: segment, query: getDeviceEntityGroupCustomer().query });
            });
            Promise.all(devEntPromises).then((deviceMapperRes) => {
                // combine to one mapper
                deviceMapperRes.forEach((mapperRes) => {
                    if (mapperRes.err === null && mapperRes.data !== null) {
                        const localMapper = mapperRes.data;
                        const devices = localMapper.getNodesOfType(DeviceClassName) || [];
                        const customers = localMapper.getNodesOfType('Customer') || [];
                        combinedMapper.loadItems(devices);
                        combinedMapper.loadItems(customers);
                        devices.forEach((dev) => {
                            // get entity
                            const entity = localMapper.getParentNodes(dev).filter((parent) => entityTypes.includes(parent.type));
                            // only care about devices that are linked to an (1) entity
                            if (entity.length === 1) {
                                const specificEntity = entity[0];
                                combinedMapper.loadItem(specificEntity);
                                // entity -> group -> customer
                                const customerFound = localMapper
                                    .getParentNodes(specificEntity)
                                    .filter((parent) => parent.type === 'Group')
                                    .map((group) => localMapper.getParentNodes(group).filter((val) => val.type === 'Customer'))
                                    .flat();
                                if (customerFound.length === 1) {
                                    // create edge between customer and device, and customer and entity
                                    const custDevEdge = constructPairingEdge(customerFound[0], dev);
                                    const custEntityEdge = constructPairingEdge(customerFound[0], specificEntity);
                                    combinedMapper.loadItem(custDevEdge);
                                    combinedMapper.loadItem(custEntityEdge);
                                }
                            }
                        });
                    }
                });
                setWideAwakeMapper(combinedMapper);
            });
        };
        if (userCustomers) {
            getAllDetails();
            updateDeviceGateways();
        }
    }, [userCustomers]);
    useEffect(() => {
        getAllUser().then((res) => {
            if (res !== null) {
                setUserList(res.Items);
            }
        });
    }, []);
    useEffect(() => {
        if (wideawakeMapper) {
            let offlineList = [];
            let onlineList = [];
            let errorList = [];
            customers.forEach((customer) => {
                const children = wideawakeMapper.getChildNodes(customer).filter((childList) => childList.type === DeviceClassName) || [];
                if (children.length === 0) {
                    return;
                }
                const offline = filterForOffline(children);
                const online = inFirstOnly(children, offline, 'id');
                const error = filterForError(children);
                if (online.length > 0) {
                    onlineList.push(customer);
                    onlineList = [...onlineList, ...online];
                }
                if (offline.length > 0) {
                    offlineList.push(customer);
                    offlineList = [...offlineList, ...offline];
                }
                if (error.length > 0) {
                    errorList.push(customer);
                    errorList = [...errorList, ...error];
                }
            });
            setOfflineComponents(offlineList);
            setOnlineComponets(onlineList);
            setErrorComponents(errorList);
        }
    }, [customers, wideawakeMapper]);
    const getDeviceAndCustomers = (header, components = [], setPopup) => {
        const customerLength = components.filter((component) => component.type === 'Customer').length;
        const deviceLength = components?.length - customerLength;
        return (_jsx(Card, { elevation: 3, sx: { flexGrow: 1, padding: 2 }, children: _jsxs(Stack, { gap: 1, children: [_jsxs(Stack, { direction: "row", justifyContent: "space-between", children: [_jsx(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: header }), setPopup && (_jsx(IconButton, { size: "small", onClick: () => setPopup(true), children: _jsx(ActionIcons.Popup, { fontSize: "small" }) }))] }), _jsxs(Stack, { children: [_jsxs(Typography, { children: [deviceLength, "\u00A0", deviceLength !== 1 ? pluralReadableTypeName(DeviceClassName) : readableTypeName(DeviceClassName)] }), _jsx(Divider, {}), _jsxs(Typography, { children: [customerLength, "\u00A0", customerLength !== 1 ? pluralReadableTypeName('Customer') : readableTypeName('Customer')] })] })] }) }));
    };
    const getErrors = (header, components = []) => {
        const devices = components.filter((component) => component.type === DeviceClassName);
        const merged = devices.reduce((errorCombined, b) => {
            const newError = b.error;
            if (newError === undefined) {
                return errorCombined;
            }
            Object.keys(newError).forEach((errorKey) => {
                if (Object.keys(errorCombined).includes(errorKey) && newError[errorKey] === 1) {
                    errorCombined[errorKey]++;
                }
                else {
                    errorCombined[errorKey] = 1;
                }
            });
            return errorCombined;
        }, {});
        return (_jsx(Stack, { children: Object.keys(merged).map((error, i, a) => {
                return (_jsxs(_Fragment, { children: [_jsxs(Typography, { children: [camelToDisplay(error), ": ", merged[error]] }, error), i !== a.length - 1 && _jsx(Divider, {})] }));
            }) }));
        return (_jsx(Card, { elevation: 3, sx: { flexGrow: 1, padding: 2 }, children: _jsxs(Stack, { gap: 1, children: [_jsx(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: header }), _jsx(Stack, { children: Object.keys(merged).map((error, i, a) => {
                            return (_jsxs(_Fragment, { children: [_jsxs(Typography, { children: [camelToDisplay(error), ": ", merged[error]] }, error), i !== a.length - 1 && _jsx(Divider, {})] }));
                        }) })] }) }));
    };
    const getEntityCounter = (header, mapper) => {
        let typeCounter = [];
        typeCounter = entityTypes
            .map((type) => {
            const count = mapper.getNodesOfType(type).length;
            return { type, count };
        })
            .filter(({ count }) => 0 < count)
            .sort((a, b) => b.count - a.count);
        return (_jsx(Card, { elevation: 3, sx: { flexGrow: 1, padding: 2 }, children: _jsxs(Stack, { gap: 1, children: [_jsx(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: header }), _jsx(Stack, { children: typeCounter.map(({ type, count }, i, a) => {
                            return (_jsxs(_Fragment, { children: [_jsxs(Typography, { children: [count, "\u00A0", count !== 1 ? pluralReadableTypeName(type) : readableTypeName(type)] }, type), i !== a.length - 1 && _jsx(Divider, {})] }));
                        }) })] }) }));
    };
    const getUsers = (header, userList = []) => {
        const lastMonthFirstDay = dayjs().subtract(1, 'month').set('date', 1).unix() * Milliseconds.SECOND;
        const seenRecently = userList.filter((val) => val.last_login && val.last_login > lastMonthFirstDay);
        return (_jsx(Card, { elevation: 3, sx: { flexGrow: 1, padding: 2 }, children: _jsxs(Stack, { gap: 1, children: [_jsx(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: header }), _jsxs(Stack, { children: [_jsxs(Typography, { children: [userList.length, "\u00A0", userList.length !== 1 ? pluralReadableTypeName(UserClassName) : readableTypeName(UserClassName)] }), _jsx(Divider, {}), _jsxs(Typography, { children: [seenRecently.length, "\u00A0Seen In The Last Month"] })] })] }) }));
    };
    const getDeviceNetwork = (header, mapper) => {
        const devices = mapper.getNodesOfType(DeviceClassName);
        const typeCounter = {};
        devices.forEach((dev) => {
            const network = dev.networkKey;
            const source = typeCounter[network];
            if (source) {
                typeCounter[network] += 1;
            }
            else {
                typeCounter[network] = 1;
            }
        });
        const sortedNetworks = Object.keys(typeCounter).toSorted((a, b) => NWName({ value: a }).localeCompare(NWName({ value: b })));
        return (_jsx(Card, { elevation: 3, sx: { flexGrow: 1, padding: 2 }, children: _jsxs(Stack, { gap: 1, children: [_jsx(Typography, { variant: "subtitle1", component: "h6", color: "primary", children: header }), _jsx(Stack, { children: sortedNetworks.map((network, i, a) => {
                            return (_jsxs(_Fragment, { children: [_jsxs(Typography, { children: [typeCounter[network], "\u00A0", NWName({ value: network })] }, network), i !== a.length - 1 && _jsx(Divider, {})] }));
                        }) })] }) }));
    };
    return (_jsxs(Stack, { gap: 2, children: [_jsx(Typography, { variant: "h5", color: "primary", children: "Venture Operation Summary" }), _jsxs(Stack, { direction: "row", alignItems: "flex-start", justifyContent: "space-evenly", gap: 2, children: [getDeviceAndCustomers('Online Devices', onlineComponents), getDeviceAndCustomers('Offline Devices', offlineComponents), getDeviceAndCustomers('Erroring Devices', errorComponents, setShowErrors), _jsx(DialogWrapper, { header: "Error Types", open: showErrors, setClosed: () => setShowErrors(false), children: getErrors('Error Types', errorComponents) })] }), _jsxs(Stack, { direction: "row", alignItems: "flex-start", justifyContent: "space-evenly", gap: 2, children: [wideawakeMapper && getEntityCounter('Types', wideawakeMapper), getUsers('Users', usersList), wideawakeMapper && getDeviceNetwork('Device Network', wideawakeMapper)] }), hasHyper && _jsx(DeviceCount, {}), _jsxs(Stack, { direction: "row", gap: 2, children: [_jsx("div", { style: { width: '50%' }, children: _jsx(ExpandableTable, { header: 'Customers with Offline Devices',
                            data: wideawakeMapper,
                            parentDefinition: customerDefinition,
                            childDefinition: deviceDefinition,
                            redirect: true,
                            childFilter: filterForOffline,
                            exportHeaders: DeviceExport,
                            elevation: 3 }) }), _jsx("div", { style: { width: '50%' }, children: _jsx(ExpandableTable, { header: 'Offline Gateways',
                            data: gatewayDevices,
                            parentDefinition: gatewayDefinition,
                            childDefinition: deviceDefinition,
                            redirect: true,
                            exportHeaders: offlineExport,
                            elevation: 3 }) })] })] }));
};
export default DeviceSummaryCard;
